import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/common/popup.scss";

const CreateModelPopup = ({ open, handleClose, onSave, initialModel }) => {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [type, setType] = useState("");
  const [availableTypes, setAvailableTypes] = useState([]);
  const [modelLimits, setModelLimits] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [error, setError] = useState("");

  const isSaveDisabled =
    !name.trim() || !provider.trim() || !apiKey.trim() || !type.trim();

  const handleFetchGeminiModels = async () => {
    if (!apiKey.trim()) {
      setError("API key is required to fetch models");
      return;
    }
    setError("");
    setLoadingModels(true);
    try {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1/models?key=${encodeURIComponent(
          apiKey.trim()
        )}`
      );
      if (!res.ok) {
        throw new Error(`Failed to fetch models: ${res.status}`);
      }
      const data = await res.json();
      const models = Array.isArray(data.models) ? data.models : [];
      const ids = models.map((m) => m.name || m.id || "").filter(Boolean);
      const limits = {};
      models.forEach((m) => {
        const key = m.name || m.id || "";
        if (key) {
          limits[key] = m.outputTokenLimit;
        }
      });
      setAvailableTypes(ids);
      setModelLimits(limits);
      if (ids.length > 0 && !type) {
        setType(ids[0]);
      }
    } catch (e) {
      console.error("Error fetching Gemini models", e);
      setError("Failed to fetch Gemini models. Check API key.");
      setAvailableTypes([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleFetchOpenAIModels = async () => {
    if (!apiKey.trim()) {
      setError("API key is required to fetch models");
      return;
    }
    setError("");
    setLoadingModels(true);
    try {
      const res = await fetch("https://api.openai.com/v1/models", {
        headers: {
          Authorization: `Bearer ${apiKey.trim()}`,
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch models: ${res.status}`);
      }
      const data = await res.json();
      const models = Array.isArray(data.data) ? data.data : [];
      const ids = models.map((m) => m.id || "").filter(Boolean);
      setAvailableTypes(ids);
      if (ids.length > 0 && !type) {
        setType(ids[0]);
      }
    } catch (e) {
      console.error("Error fetching OpenAI models", e);
      setError("Failed to fetch OpenAI models. Check API key.");
      setAvailableTypes([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleFetchAnthropicModels = async () => {
    if (!apiKey.trim()) {
      setError("API key is required to fetch models");
      return;
    }
    setError("");
    setLoadingModels(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/models", {
        headers: {
          "x-api-key": apiKey.trim(),
          "anthropic-version": "2023-06-01",
        },
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch models: ${res.status}`);
      }
      const data = await res.json();
      const models = Array.isArray(data.data) ? data.data : [];
      const ids = models.map((m) => m.id || "").filter(Boolean);
      setAvailableTypes(ids);
      if (ids.length > 0 && !type) {
        setType(ids[0]);
      }
    } catch (e) {
      console.error("Error fetching Anthropic models", e);
      setError("Failed to fetch Anthropic models. Check API key.");
      setAvailableTypes([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleFetchAzureModels = async () => {
    setError(
      "Fetching Azure models is not configured in this UI yet. Please select the model type manually."
    );
  };

  const handleFetchModels = async () => {
    if (!provider) {
      setError("Please select a provider first");
      return;
    }

    if (provider === "gemini") {
      await handleFetchGeminiModels();
    } else if (provider === "openai") {
      await handleFetchOpenAIModels();
    } else if (provider === "anthropic") {
      await handleFetchAnthropicModels();
    } else if (provider === "azure") {
      await handleFetchAzureModels();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !provider.trim() || !apiKey.trim() || !type.trim())
      return;
    const payload = {
      name: name.trim(),
      api_key: apiKey.trim(),
      provider,
      type: type.trim(),
      max_tokens: modelLimits[type] || undefined,
      is_active: true,
    };
    if (onSave) {
      await onSave(payload);
    }
  };

  useEffect(() => {
    if (!provider || !apiKey.trim()) return;
    handleFetchModels();
  }, [provider, apiKey]);

  useEffect(() => {
    if (open) {
      if (initialModel) {
        setName(initialModel.name || "");
        setProvider(initialModel.provider || "");
        setApiKey(initialModel.api_key || "");
        setType(initialModel.type || "");
        setAvailableTypes(initialModel.type ? [initialModel.type] : []);
      } else {
        setName("");
        setProvider("");
        setApiKey("");
        setType("");
        setAvailableTypes([]);
      }
      setModelLimits({});
      setLoadingModels(false);
      setError("");
    }
  }, [open, initialModel]);

  if (!open) return null;

  return createPortal(
    <div className="common-popup-overlay high-z-index" onClick={handleClose}>
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          <h2>{initialModel ? "Edit Model" : "Create Model"}</h2>
          <button
            type="button"
            className="popup-close-button"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          <form onSubmit={handleSubmit}>
            <div className="popup-form-group">
              <label>Name</label>
              <input
                type="text"
                className="popup-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Model display name"
              />
            </div>

            <div className="popup-form-group">
              <label>Provider</label>
              <select
                className="popup-select"
                value={provider}
                onChange={(e) => {
                  const value = e.target.value;
                  setProvider(value);
                  setAvailableTypes([]);
                  setType("");
                  setModelLimits({});
                  setError("");
                }}
              >
                <option value="">Select provider</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="azure">Azure</option>
                <option value="gemini">Gemini</option>
              </select>
            </div>

            <div className="popup-form-group">
              <label>API Key</label>
              <input
                type="text"
                className="popup-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Provider API Key"
                disabled={!provider}
              />
            </div>

            <div className="popup-form-group">
              <label>Type</label>
              <div className="popup-form-group">
                <select
                  className="popup-select"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={
                    !provider ||
                    loadingModels ||
                    (provider !== "azure" && availableTypes.length === 0)
                  }
                >
                  <option value="">
                    {loadingModels ? "Loading models..." : "Select model type"}
                  </option>
                  {availableTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && <div className="popup-error-message">{error}</div>}

            <div className="popup-actions">
              <button
                type="submit"
                className="popup-submit-button"
                disabled={isSaveDisabled}
              >
                Save Model
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateModelPopup;
